import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import NormSearchIcon from './icons/NormSearchIcon';
import NormUserIcon from './icons/NormUserIcon';
import NormCartIcon from './icons/NormCartIcon';
import { useCart } from '../context/CartContext';
import MiniCart from './MiniCart';
import { useAuth } from '../context/AuthProvider';
import { toast } from 'react-hot-toast';
import { User, ShoppingBag, Home, LogOut } from 'lucide-react';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cart } = useCart();
  const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const { user, logout } = useAuth();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null); // ✅ To detect hover outside
  let hoverTimeout = useRef(null);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowSearch(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length > 1) {
        fetch(`/api/products/search?q=${searchQuery}`)
          .then(res => res.json())
          .then(data => setSuggestions(data))
          .catch(() => setSuggestions([]));
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSearch &&
        searchInputRef.current &&
        !searchInputRef.current.closest('.search-container')?.contains(event.target)
      ) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearch]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 shadow-sm py-3" style={{ fontFamily: "'Montserrat', sans-serif", backgroundColor: '#3b3d6e' }}>
        <div className="w-full flex items-center justify-between px-4 md:px-14">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link to="/">
              <img src="/assets/logo.png" alt="MORY Logo" className="h-6 object-contain" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex justify-center gap-8 uppercase"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              letterSpacing: '2.4px',
              lineHeight: '19.8px',
              color: 'rgb(250, 250, 250)'
            }}
          >
            <Link to="/shop" className="nav-link">All Products</Link>
            <Link to="/shop?category=ring" className="nav-link">Rings</Link>
            <Link to="/shop?category=bracelet" className="nav-link">Bracelets</Link>
            <Link to="/shop?category=necklace" className="nav-link">Necklaces</Link>
            <Link to="/shop?category=earring" className="nav-link">Earrings</Link>
            <Link to="/live-gold" className="nav-link">Gold Rate</Link>

          </div>

          {/* Mobile Nav Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden absolute top-full left-0 w-full bg-[#3b3d6e] flex flex-col items-center py-4 space-y-4 z-40"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '2.4px',
                lineHeight: '19.8px',
                color: 'rgb(250, 250, 250)'
              }}
            >
              <Link to="/shop" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Collections</Link>
              <Link to="/shop?category=ring" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Rings</Link>
              <Link to="/shop?category=bracelet" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Bracelets</Link>
              <Link to="/shop?category=necklace" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Necklaces</Link>
              <Link to="/shop?category=earring" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Earrings</Link>
              <Link to="/live-gold" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Gold Rate</Link>

            </div>
          )}

          {/* Icons */}
          <div className="flex items-center gap-[30px]">
            <button onClick={() => setShowSearch(true)}>
              <NormSearchIcon className="h-5 w-5 text-[rgb(250,250,250)] hover:opacity-80 transition" />
            </button>
            {/* Account Icon & Dropdown */}
            {user ? (
              <div
                className="relative group"
                ref={userDropdownRef}
                onMouseEnter={() => {
                  clearTimeout(hoverTimeout.current);
                  setShowUserDropdown(true);
                }}
                onMouseLeave={() => {
                  hoverTimeout.current = setTimeout(() => {
                    setShowUserDropdown(false);
                  }, 200); // smooth fade delay like cart
                }}
              >

                <NormUserIcon className="h-5 w-5 text-[rgb(250,250,250)] hover:opacity-80 transition cursor-pointer" />
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-52 bg-white shadow-xl rounded-lg overflow-hidden text-sm z-50 border divide-y divide-gray-100">
                    <Link to="/my-orders" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-100">
                      <ShoppingBag size={18} /> <span>My Orders</span>
                    </Link>
                    <Link to="/account" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-100">
                      <User size={18} /> <span>Profile</span>
                    </Link>
                    <Link to="/account/addresses" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-100">
                      <Home size={18} /> <span>Addresses</span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        toast.success('Logged out successfully');
                        setShowUserDropdown(false);
                        navigate('/');
                      }}
                      className="flex items-center gap-3 w-full text-left px-4 py-3 hover:bg-gray-100 text-red-600 font-medium"
                    >
                      <LogOut size={18} /> <span>Logout</span>
                    </button>
                  </div>

                )}
              </div>
            ) : (
              <Link to="/auth">
                <NormUserIcon className="h-5 w-5 text-[rgb(250,250,250)] hover:opacity-80 transition" />
              </Link>
            )}


            <div
              className="relative group"
              onMouseEnter={() => setShowMiniCart(true)}
              onMouseLeave={() => setShowMiniCart(false)}
            >  <Link to="/cart">
                <NormCartIcon className="h-5 w-5 text-[rgb(250,250,250)] hover:opacity-80 transition" />
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              </Link>
              <MiniCart isOpen={showMiniCart} />
            </div>

          </div>


          {/* Mobile toggle */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Search Overlay (Single, functional) */}
      {showSearch && (
        <div className="fixed top-[49px] left-0 right-0 z-50 bg-white  px-6 py-0 text-black text-center flex flex-col items-center justify-start shadow-md">
          <div className="relative w-full max-w-xl search-container">
            <input
              type="text"
              autoFocus
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
                if (e.key === 'Escape') setShowSearch(false);
              }}
              ref={searchInputRef}
              className="w-full text-center border-b border-black text-lg tracking-widest py-2 outline-none"
            />
            <button
              className="absolute right-2 top-2 text-lg text-gray-500 hover:text-black"
              onClick={() => setShowSearch(false)}
            >
              ✕
            </button>

            {suggestions.length > 0 && (
              <ul className="absolute left-0 right-0 top-full bg-white border border-gray-200 rounded shadow z-50 text-left text-sm mt-1 max-h-[300px] overflow-y-auto">
                {suggestions.map((item) => (
                  <li
                    key={item._id}
                    onClick={() => {
                      navigate(`/shop?search=${encodeURIComponent(item.name)}`);
                      setShowSearch(false);
                      setSearchQuery('');
                    }}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <strong>{item.name}</strong>
                    <div className="text-xs text-gray-500">{item.productType?.join(', ')}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>


          <button
            onClick={handleSearch}
            className="mt-4 bg-black text-white px-6 py-2 text-sm uppercase tracking-wide md:hidden"
          >
            Search
          </button>
        </div>
      )}
    </>
  );
};

export default Navbar;
